import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { SEO } from '../components/SEO';
import { User, Mail, Phone, Shield, Download, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ApplyPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e, requestType = 'application') => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/api/applications`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        request_type: requestType
      });

      if (response.data.success) {
        if (response.data.brochure_url) {
          navigate(`/thank-you?type=brochure&brochure=${encodeURIComponent(response.data.brochure_url)}`);
        } else {
          navigate('/thank-you?type=application');
        }
      }
      
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark">
      <SEO 
        page="apply"
        title="Apply Now | Need A Hair Makeover Franchise"
        description="Apply for a Need A Hair Makeover franchise opportunity. Fill out your details to receive your franchise information pack or submit your application."
      />
      
      {/* Simple Header */}
      <header className="bg-white py-4 px-6">
        <div className="max-w-4xl mx-auto flex justify-center">
          <a href="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_hair-opportunity/artifacts/ro1cz7gd_GIF_20250914_153723_283.gif"
              alt="Need A Hair Makeover Franchise"
              className="h-16"
            />
          </a>
        </div>
      </header>

      {/* Form Section */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Start Your Journey Today
            </h1>
            <p className="text-xl text-gray-300">
              Get your franchise information pack or apply for your franchise opportunity
            </p>
          </div>

          {/* Form Card */}
          <Card className="bg-white shadow-2xl border-0">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-dark mb-2">
                  Get Your Franchise Information
                </h2>
                <p className="text-subtle">
                  Fill out your details below to receive your franchise brochure or submit your application
                </p>
              </div>

              <form onSubmit={(e) => handleSubmit(e, 'application')} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Full Name
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                    disabled={isSubmitting}
                    data-testid="apply-name-input"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-accent-primary focus:ring-0 transition-colors duration-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address
                  </label>
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    disabled={isSubmitting}
                    data-testid="apply-email-input"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-accent-primary focus:ring-0 transition-colors duration-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                    disabled={isSubmitting}
                    data-testid="apply-phone-input"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-accent-primary focus:ring-0 transition-colors duration-200"
                  />
                </div>

                {/* Helper Text */}
                <div className="bg-gray-50 rounded-lg p-3 text-center text-sm text-subtle">
                  💬 If form does not work, click on &quot;Have a Chat&quot; before submitting
                </div>

                {/* Buttons */}
                <div className="space-y-3 pt-2">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="apply-submit-btn"
                    className="w-full bg-white border-2 border-accent-primary text-accent-primary hover:bg-accent-primary hover:text-white py-4 rounded-lg text-lg font-semibold transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Apply for Franchise Opportunity'
                    )}
                  </Button>

                  <Button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'brochure')}
                    disabled={isSubmitting}
                    data-testid="apply-brochure-btn"
                    className="w-full bg-white border-2 border-accent-primary text-accent-primary hover:bg-accent-primary hover:text-white py-4 rounded-lg text-lg font-semibold transition-all duration-300"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    {isSubmitting ? 'Submitting...' : 'Get Free Franchise Brochure (Instant Download)'}
                  </Button>
                </div>

                {/* Privacy Notice */}
                <div className="text-center pt-4 space-y-2">
                  <div className="flex items-center justify-center text-subtle text-sm">
                    <Shield className="w-4 h-4 mr-2" />
                    We respect your privacy. Your details are safe with us.
                  </div>
                  <p className="text-xs text-subtle-light">
                    By submitting this form, you agree to our{' '}
                    <a href="/privacy" className="text-accent-primary hover:underline">Privacy Policy</a>
                    {' '}and consent to us contacting you about franchise opportunities. You can unsubscribe at any time.
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Back Link */}
          <div className="text-center mt-8">
            <a 
              href="/" 
              className="text-gray-400 hover:text-white transition-colors"
            >
              ← Back to Homepage
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplyPage;
